<template>
  <div :class="{ 'c-disabled-block' : readonly, 'single-setting--bad-value': valueNeedsAttention }">
    <div class="c-disabled-block__message" v-if="forAdminOnly">
      <icon class="u-mr2" name="lock"></icon>
       Setting is visible for Administrators Only.
    </div>
    <div class="c-disabled-block__message" v-if="readonly && !isDeprecated">
      <icon class="u-mr2" name="exclamation-triangle"></icon>
      Setting is 'Read Only'. Please contact BriteApps team for assistance.
    </div>
    <div class="c-disabled-block__message" v-if="isDeprecated">
      <icon class="u-mr2" name="exclamation-triangle"></icon>
      Setting is 'No longer used'.
    </div>
    <div class="c-disabled-block__message single-setting__bad-value" v-if="valueNotUsable">
      <icon class="u-mr2" name="exclamation-triangle"></icon>
      Stored value "{{storedValue()}}" {{notUsableReason}}.
      <span v-if="readonly">Please contact BriteApps team for assistance.</span>
      <span v-else>Saving any value will replace it.</span>
    </div>
    <div class="c-disabled-block__message single-setting__bad-value" v-else-if="valueOutOfRange">
      <icon class="u-mr2" name="exclamation-triangle"></icon>
      Stored value is outside the allowed range ({{allowedRangeText}}). It is shown as stored;
      <span v-if="readonly">please contact BriteApps team for assistance.</span>
      <span v-else>saving any value will bring it back into range.</span>
    </div>
      <div class="single-setting__title u-mb1">
        <div class="single-setting__name">
          {{setting.name}}
          <el-popover
                  placement="top-start"
                  :title="setting.name"
                  width="700"
                  trigger="hover">
            <icon class="u-mr1 setting-help__icon" slot="reference" scale="1" :name="'question-circle'"></icon>
            <div class="setting-help">
              <div class="single-setting__description">
                <span v-html="setting.description"/>
              </div>
            </div>
          </el-popover>
          <em class="u-text--sm u-text--light u-ml3">{{setting.section.slug}}.{{setting.slug}}</em>
        </div>
      </div>




    <div class="u-mt2 c-disabled-block__message" v-if="(carrierAccess === 'N' || carrierAccess === 'R' ) && user.is_superuser || setting.is_global">
      <icon class="u-mr2" name="lock"></icon>
      Setting for administrators Only.
    </div>
      <div class="single-setting__value u-mb4" :key="valueEpoch">
        <div class="setting--boolean" v-if="setting.stype === 'BLN'">
          <el-switch :disabled="readonly" v-model="settingValue"></el-switch>
        </div>
        <div class="setting--string" v-if="setting.stype === 'STR' || setting.stype === 'URL'">
          <el-input :disabled="readonly" v-model="settingValueComputed" @change="updateTextSetting" @blur="updateTextSetting"></el-input>
        </div>
        <div class="setting--string" v-if="setting.stype === 'LTX'">
          <el-input type="textarea" autosize :rows="3" :disabled="readonly" v-model="settingValueComputed" @change="updateTextSetting" @blur="updateTextSetting"></el-input>
        </div>
        <div class="setting--markdown" v-if="setting.stype === 'MKD'">
          <el-input :disabled="readonly" type="textarea" v-model="settingValueComputed" @change="updateTextSetting" @blur="updateTextSetting"></el-input>
        </div>
        <div class="setting--int" v-if="setting.stype === 'INT'">
          <!-- :value + @change rather than v-model. el-input-number's value watcher is immediate and
               emits `input` on mount after clamping to :min/:max, so a v-model setter would save a
               clamped value with nobody having touched the control. `change` is emitted only for
               real edits. -->
          <el-input-number :disabled="readonly" :value="settingValue" @change="updateIntSetting"
                           @input.native="noteTextEdit"
                           :min="range.min" :max="range.max"></el-input-number>
        </div>
        <div class="setting--decimal" v-if="setting.stype === 'DEC'">
          <!-- bound like the other el-input fields: v-model="settingValue" dispatched a save per
               keystroke, and with the store only moving on the server's answer, an earlier response
               would roll the field back to a prefix mid-typing -->
          <el-input :disabled="readonly" v-model="settingValueComputed" @change="updateTextSetting" @blur="updateTextSetting"></el-input>
        </div>
        <div class="setting--time" v-if="setting.stype === 'TIM'">
          <el-time-select :disabled="readonly" v-model="settingValue"
                          :picker-options="{
                            start: '06:00',
                            step: '00:15',
                            end: '23:30'
                          }"></el-time-select>
      </div>
      <div class="" v-if="setting.stype === 'SEL'">
        <el-select v-model="settingValue">
          <el-option
                  v-for="opt in options"
                  :value="opt.value"
                  :label="opt.text"
                  :key="opt.value">
          </el-option>
        </el-select>
      </div>
      <div class="u-mt4">

        <!--{{setting.default}}-->
      </div>
    </div>
  </div>
</template>

<script>
import {mapActions, mapState} from 'vuex'
import {
  forgetSaveState,
  saveKeyFor,
  saveStateFor,
} from '@/modules/settings/save-state'
import {
  allowedRangeText,
  asWholeNumber,
  declaredRange,
  shownRange,
  valueOutOfRange,
  withinDeclaredRange,
  WHOLE_NUMBER_SYNTAX,
} from '@/modules/settings/setting-value'

// The stypes whose control reads settingValue rather than settingValueClone: the switch, the
// spinner, the time picker and the select. settingValue does not change when a save is refused, so
// nothing in their render tree is dirty and they keep what was entered unless the element itself is
// replaced. Every other stype is an el-input bound to settingValueClone, which is reactive all the
// way to the DOM node (el-input watches nativeInputValue and writes it), so assigning the clone is
// enough and replacing the element would only cost the user their caret.
const STORED_VALUE_CONTROLS = ['BLN', 'INT', 'TIM', 'SEL']

export default {
  props: ['setting', 'isGlobal'],
  name: 'ba-single-setting',
  data () {
    let carrierAccess = this.setting.carrier_access
    // Captured here rather than read from $route later. vue-router advances $route *before* the
    // outgoing view is destroyed, so on a company switch beforeDestroy would otherwise build a key
    // for the company being navigated to -- creating an `undefined:<settingId>` entry and deleting
    // that, while the real one it meant to remove stays in the map. Verified against vue-router
    // 3.6.5: created saw the company, beforeDestroy saw undefined.
    let companyId = this.$route.params.companyId
    let saveKey = saveKeyFor(companyId, this.setting.id)
    // seed from what was last asked for when a save is still in flight: the store does not move
    // until the server answers, so it would otherwise hand a reopened section the old value --
    // which a later blur would then post back, undoing the edit that was already on its way
    let pending = saveStateFor(saveKey).requested
    let settingValueClone = pending !== null
      ? pending
      : (this.setting.overridden ? this.setting.overridden.value : this.setting.default)
    return {
      companyId,
      saveKey,
      settingValueClone: settingValueClone,
      initialSettingValue: settingValueClone,
      carrierAccess,
      // bumped when the server refuses a value, to remount the inputs below so they re-read the
      // stored value instead of keeping what the user typed
      valueEpoch: 0,
      // the text a person last put in the spinner, recorded by noteTextEdit and matched against
      // the value in updateIntSetting
      lastTextEdit: null,
    }
  },
  beforeDestroy () {
    // The record exists to outlive this instance while a save is in flight. Nothing is in flight,
    // so there is nothing for it to carry -- and both collapsing a section and switching company
    // destroy instances routinely, so without this the map keeps one entry per (company, setting)
    // ever touched for the lifetime of the tab. The key comes from data(), not from $route: see
    // there for why reading it here would clear the wrong entry on a company switch.
    //
    // Safe precisely because `requested` is null: no request is outstanding, so no refusal can
    // arrive later expecting the counter it would have been recorded on.
    if (this.saveState.requested === null) {
      forgetSaveState(this.saveKey)
    }
  },
  watch: {
    // Keyed on the counter, not the value: the same value failing twice is a second refusal, and
    // watching the value alone would miss it.
    'saveState.refusals' () {
      let refused = this.saveState.refusedValue
      // Only take the field back if it still shows what was refused. A text field's v-model writes
      // every keystroke into settingValueClone without dispatching a save, so the user can be
      // part-way through a correction -- that newer text is theirs, not something to throw away.
      if (this.settingValueClone === refused) {
        // the store never moved, so read the value back off it
        this.settingValueClone = this.storedValue()
        this.initialSettingValue = this.settingValueClone
      }
      // Only the controls that read settingValue need replacing: theirs is the render tree that
      // stays clean when a save is refused. Doing it to a text field would replace the element the
      // user may still be typing into, taking their focus and caret with it -- and for nothing,
      // since the clone above already reached the DOM.
      if (this.bindsStoredValue) {
        this.valueEpoch += 1
      }
    },
  },
  computed: {
    ...mapState('shared', ['user']),
    readonly () {
      if (!this.user) {
        return true
      }
      if (this.setting.is_global && !this.isGlobal) {
        // do not allow to edit global setting, unless this component is rendered on global setting tab
        return true
      }

      if (this.isDeprecated) {
        return true
      }

      return !this.user.is_superuser && (this.carrierAccess === 'R' || this.carrierAccess === 'N')
    },
    forAdminOnly () {
      return this.carrierAccess === 'AO'
    },
    isDeprecated () {
      return this.carrierAccess === 'D'
    },
    declaredRange () {
      return declaredRange(this.setting.metadata)
    },
    storedNumber () {
      // deliberately reads the store, not the pending value: this drives the warnings, which are
      // about what is actually stored
      if (this.setting.stype !== 'INT') {
        return null
      }
      return this.asWholeNumber(this.storedValue())
    },
    valueNotUsable () {
      // an INT setting whose stored value this field cannot present as an ordinary number, either
      // because it is not a whole number or because it is too large to show exactly
      return this.setting.stype === 'INT' && this.storedNumber === null
    },
    notUsableReason () {
      // a value the server would accept but JavaScript cannot hold is a different complaint from
      // one that is not a number at all, and saying the wrong one is its own small lie
      return WHOLE_NUMBER_SYNTAX.test(String(this.storedValue()).trim())
        ? 'is too large for this field to show exactly'
        : 'is not a whole number, so the field below cannot show it'
    },
    valueNeedsAttention () {
      return this.valueNotUsable || this.valueOutOfRange
    },
    bindsStoredValue () {
      return STORED_VALUE_CONTROLS.indexOf(this.setting.stype) !== -1
    },
    saveState () {
      return saveStateFor(this.saveKey)
    },
    valueOutOfRange () {
      return valueOutOfRange(this.declaredRange, this.storedNumber)
    },
    allowedRangeText () {
      return allowedRangeText(this.declaredRange)
    },
    range () {
      return shownRange(this.declaredRange, this.storedNumber)
    },
    options () {
      let result = []

      while (true) {
        let metadata = this.setting.metadata
        if (!metadata || !metadata.options) {
          break
        }

        let opts = metadata.options

        if (!opts) {
          break
        }

        result = opts

        break
      }

      return result
    },
    settingValueComputed: {
      get () {
        return this.settingValueClone
      },
      set (updatedSettingValue) {
        this.settingValueClone = updatedSettingValue
      },
    },
    settingValue: {
      get () {
        // pending first. The store does not move until the server answers, so an earlier response
        // landing mid-edit would otherwise roll the control back to a value the user has already
        // moved past -- and their next action would build on that stale one and collide with the
        // request still queued.
        let val = this.pendingOrStoredValue()

        let stype = this.setting.stype
        if (stype === 'BLN') {
          if (val === 'true') {
            return true
          } else {
            return false
          }
        } else if (stype === 'INT') {
          // undefined leaves el-input-number empty; returning a truncated number here would be the
          // same lie the note above the field is there to point out
          let parsed = this.asWholeNumber(val)
          return parsed === null ? undefined : parsed
        }
        return val
      },
      set (value) {
        this.saveSetting({
          setting: this.setting,
          newValue: value.toString(),
          companyId: this.companyId,
          oldValue: this.pendingOrStoredValue(),
        })
      },
    },
  },
  methods: {

    ...mapActions('settings', ['updateSingleSetting']),
    ...mapActions('globalSettings', ['updateGlobalSetting']),
    storedValue () {
      return this.setting.overridden ? this.setting.overridden.value : this.setting.default
    },
    asWholeNumber (value) {
      return asWholeNumber(value)
    },
    pendingOrStoredValue () {
      // what the server was last asked for, falling back to what it is known to hold. Read off the
      // shared record rather than this instance, so a reopened section sees a request its
      // predecessor started -- and sees it clear when that request settles.
      let requested = this.saveState.requested
      return requested !== null ? requested : this.storedValue()
    },
    withinDeclaredRange (value) {
      return withinDeclaredRange(this.declaredRange, value)
    },
    noteTextEdit (event) {
      // The *native* input event, which fires only when a person changes the text: typing, pasting
      // from the context menu, dragging text in, IME, autofill. The steppers do not reach it --
      // el-input-number sets currentValue through Vue, and assigning an input's value in code does
      // not fire `input` -- and neither do its arrow keys, which call the same increase/decrease.
      //
      // The text itself, not a boolean. An edit does not always produce a `change` to consume the
      // record: setCurrentValue returns early when the value has not moved, so retyping the shown
      // number, or typing one that clamps back to it, emits nothing at all. A flag set by those
      // stayed set, and the next click inherited it. A value can only be claimed as typed by the
      // edit that actually produced it.
      this.lastTextEdit = event.target.value
    },
    updateIntSetting (value) {
      // Typed only if this value is the one the last edit put in the field. Compared as numbers,
      // through the same conversion el-input-number itself applies to the text -- `value === '' ?
      // undefined : Number(value)` (input-number.vue:259) -- so the match holds by construction
      // rather than by the text happening to spell the number the same way. Comparing the strings
      // read `059`, `59.0` and `+59` as nudges and snapped them, which is the silent substitution
      // this whole distinction exists to prevent.
      //
      // Consumed either way, and harmless if it is never consumed at all: a stale record cannot
      // match a number it did not produce.
      let edited = this.lastTextEdit === null || this.lastTextEdit === ''
        ? undefined
        : Number(this.lastTextEdit)
      let typed = edited !== undefined && edited === value
      this.lastTextEdit = null

      if (value === undefined || value === null) {
        // the spinner was cleared rather than set to a number
        return
      }
      // The control's range is widened to show a stored value that breaks its declared bounds, so
      // the steppers can reach numbers the server will refuse: decrementing from a stored 60 with
      // a declared maximum of 30 offers 59, which comes back rejected and leaves 60 in place, so
      // the buttons cannot walk the value back into range at all. A nudge off an out-of-range
      // value therefore snaps to the nearest declared bound -- the only useful place it can mean.
      //
      // A typed value is left alone. Quietly storing 30 because someone typed 45 would be the field
      // disagreeing with what was entered, which is the thing this whole change exists to stop; it
      // goes to the server as typed and its refusal is shown instead.
      //
      // Which of the two it was cannot be read off the number alone -- 59 in a field showing 60 is
      // one away from it whether it was clicked or typed -- so noteTextEdit records the text as it
      // is entered and `typed` above asks whether this value came from it.
      this.saveSetting({
        setting: this.setting,
        newValue: (typed ? value : this.withinDeclaredRange(value)).toString(),
        companyId: this.companyId,
        oldValue: this.pendingOrStoredValue(),
      })
    },
    saveSetting (payload) {
      let save = this.saveState
      if (payload.newValue === payload.oldValue) {
        // Nothing to ask for. This has to be caught before the sequence moves: text inputs fire
        // @change and @blur with the same value, and if that second no-op bumped the sequence, the
        // first request's failure would look superseded and be swallowed without a word.
        return save.chain
      }

      let seq = ++save.seq
      save.requested = payload.newValue
      // one request at a time for this setting: two in flight can complete out of order and leave
      // the store holding the earlier of the two values. The queue hangs off the record rather
      // than the instance, so collapsing the section mid-save does not start a second one.
      save.chain = this.sendSave(save, seq, payload)
      return save.chain
    },
    async sendSave (save, seq, payload) {
      // save.chain is always another sendSave, and sendSave handles its own failure, so awaiting
      // it here cannot reject and cannot poison the queue behind it
      await save.chain

      try {
        // The component is shared: global-settings.page.vue renders it with :isGlobal="true", and
        // that screen edits Setting.default through a different endpoint. Branching here rather
        // than at the call sites means every stype takes the same route -- before this, only the
        // el-input types checked isGlobal, so a BLN, INT, SEL or TIM on the global page posted to
        // `company/undefined/set_carrier_setting/` and failed. It always had; it merely failed
        // silently while the store moved anyway.
        await (this.isGlobal ? this.updateGlobalSetting(payload) : this.updateSingleSetting(payload))
        if (seq === save.seq) {
          save.requested = null
        }
      } catch (error) {
        if (seq !== save.seq) {
          // a newer edit has gone out since; its outcome owns the field, not this one's
          return
        }
        save.requested = null
        let response = error.response || {}
        // prefer the server's own messages: axiosWrapper fills in a generic `message` when the
        // body carries none, which would otherwise mask the specific reason
        let message = (response.messages || [])[0] || response.message || 'Setting was not saved'
        this.$notify.error({title: 'Setting not saved', message})

        // Record the refusal rather than resetting the field here. `this` is whichever instance
        // sent the request, and collapsing the section destroys it -- its replacement copied the
        // pending value into its own clone and would go on showing the refused value, and
        // resubmit it on the next blur. The watcher below runs on whichever instance is mounted.
        save.refusedValue = payload.newValue
        save.refusals += 1
      }
    },
    updateTextSetting (e) {
      let value = e.target ? e.target.value : e
      // One path for both screens now. The global branch used to dispatch straight at the store,
      // which skipped the queue, the sequence guard and the revert -- so a refusal there was as
      // invisible as the one this PR exists to surface.
      this.saveSetting({
        setting: this.setting,
        companyId: this.companyId,
        newValue: value,
        oldValue: this.pendingOrStoredValue(),
      })
      // update ui state
      this.settingValueClone = value
      this.initialSettingValue = value
    },
  },
}
</script>

<style scoped>
  .single-setting__title {
    display: flex;
  }

  .single-setting__name {
    flex: auto;
    line-height: 30px;
  }

  .single-setting__description {
    /* Treat \r\n as line breaks */
    white-space: pre-line;
    word-break: normal;
  }

  .setting-help__icon {
    border-radius: 4px;
    position: relative;
    top: 0.4em;
    color: #455b5f;
    margin: 0 0 3px 10px;
  }

  .setting-help__icon:hover {
    color: #d8dce5;
  }

  .setting-help {
    line-height: 2em;
    word-break: keep-all;
  }

  .single-setting--bad-value {
    background: #fef0f0;
    border: 1px solid #fbc4c4;
    border-radius: 4px;
    padding: .5em;
  }

  .single-setting__bad-value {
    color: #f56c6c;
  }
</style>

<style>
  .el-popover {
    text-align: left !important;
  }

  .el-popover__title {
    text-decoration: underline;
  }

</style>
